/**
 * Employee statutory ID helpers (UAN / PAN / Aadhaar / PF / ESIC / LWF).
 * Frontend rules must match backend/services/employee_statutory_validators.py.
 */

const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export function verhoeffCheck(digits) {
  const s = String(digits || "");
  if (!/^\d+$/.test(s)) return false;
  let c = 0;
  const reversed = s.split("").reverse();
  for (let i = 0; i < reversed.length; i += 1) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][Number(reversed[i])]];
  }
  return c === 0;
}

export const STATUTORY_CODE_MESSAGES = {
  uan_must_be_12_digits: "UAN must be exactly 12 digits (numbers only)",
  uan_duplicate: "This UAN is already in use in your organisation",
  pan_format_invalid: "PAN must match format AAAAA9999A",
  pan_duplicate: "This PAN is already in use in your organisation",
  aadhaar_must_be_12_digits: "Aadhaar must be exactly 12 digits (numbers only)",
  aadhaar_checksum_invalid:
    "Aadhaar checksum is invalid. Recheck the 12-digit number, or leave it blank.",
  aadhaar_duplicate: "This Aadhaar is already in use in your organisation",
  pf_number_format_invalid:
    "PF Member ID allows letters, numbers, / and - only (maximum 30 characters)",
  esic_number_format_invalid: "ESIC number must be 10 to 17 digits",
  employment_category_invalid: "Select a valid employment category",
  uan_required_when_pf_applicable: "UAN Number is required when PF applicable is ticked",
  pf_number_required_when_pf_applicable: "PF Member ID is required when PF applicable is ticked",
  esic_required_when_esi_applicable: "ESIC Insurance Number is required when ESI applicable is ticked",
};

export function statutoryCodeMessage(code, fallback = "") {
  if (!code) return fallback;
  return STATUTORY_CODE_MESSAGES[code] || fallback || String(code);
}

export function blankToNull(value) {
  const s = String(value ?? "").trim();
  return s || null;
}

export function sanitizeStatutoryValue(key, raw) {
  const value = String(raw ?? "");
  switch (key) {
    case "uan_number":
    case "aadhaar":
      return value.replace(/\D/g, "").slice(0, 12);
    case "esic_number":
      return value.replace(/\D/g, "").slice(0, 17);
    case "pan":
      return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    case "pf_number":
      return value.replace(/[^A-Za-z0-9/-]/g, "").toUpperCase().slice(0, 30);
    case "lwf_number":
    case "pt_number":
      return value.replace(/[^A-Za-z0-9-]/g, "").slice(0, 30);
    default:
      return value;
  }
}

export function validateStatutoryField(key, value) {
  const v = String(value || "").trim();
  if (!v) return "";
  switch (key) {
    case "uan_number":
      if (!/^\d{12}$/.test(v)) return STATUTORY_CODE_MESSAGES.uan_must_be_12_digits;
      return "";
    case "pan":
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v)) return STATUTORY_CODE_MESSAGES.pan_format_invalid;
      return "";
    case "aadhaar":
      if (!/^\d{12}$/.test(v)) return STATUTORY_CODE_MESSAGES.aadhaar_must_be_12_digits;
      if (!verhoeffCheck(v)) return STATUTORY_CODE_MESSAGES.aadhaar_checksum_invalid;
      return "";
    case "pf_number":
      if (v.length > 30 || !/^[A-Za-z0-9/-]+$/.test(v)) {
        return STATUTORY_CODE_MESSAGES.pf_number_format_invalid;
      }
      return "";
    case "esic_number":
      if (!/^\d{10,17}$/.test(v)) return STATUTORY_CODE_MESSAGES.esic_number_format_invalid;
      return "";
    case "lwf_number":
    case "pt_number":
      if (!/^[A-Za-z0-9-]+$/.test(v)) return "LWF / PT number allows letters, numbers and hyphen only";
      return "";
    case "effective_from":
      if (!v) return "Effective from is required when adding statutory details";
      return "";
    default:
      return "";
  }
}

export function isStatutoryFieldValid(key, value) {
  const v = String(value || "").trim();
  if (!v) return false;
  return !validateStatutoryField(key, v);
}

/** Required-field rules driven by PF / ESI applicability checkboxes. */
export function statutoryApplicabilityErrors(form = {}) {
  const errors = {};
  if (form.pf_member) {
    if (!String(form.uan_number || "").trim()) {
      errors.uan_number = STATUTORY_CODE_MESSAGES.uan_required_when_pf_applicable;
    }
    if (!String(form.pf_number || "").trim()) {
      errors.pf_number = STATUTORY_CODE_MESSAGES.pf_number_required_when_pf_applicable;
    }
  }
  if (form.esi_applicable) {
    if (!String(form.esic_number || "").trim()) {
      errors.esic_number = STATUTORY_CODE_MESSAGES.esic_required_when_esi_applicable;
    }
  }
  return errors;
}

const DEFAULT_STATUTORY_ID_KEYS = [
  "uan_number",
  "pan",
  "aadhaar",
  "pf_number",
  "esic_number",
  "lwf_number",
  "pt_number",
];

/** Format errors plus PF/ESI required-when-ticked errors. */
export function collectStatutoryFormErrors(form = {}, keys = DEFAULT_STATUTORY_ID_KEYS) {
  const errors = {};
  keys.forEach((key) => {
    const msg = validateStatutoryField(key, form[key]);
    if (msg) errors[key] = msg;
  });
  Object.entries(statutoryApplicabilityErrors(form)).forEach(([key, msg]) => {
    if (!errors[key]) errors[key] = msg;
  });
  return errors;
}

/** Map API `detail.errors` (tuples or objects) onto field → user message. */
export function statutoryErrorsFromApiDetail(detail) {
  const errors = {};
  const list = Array.isArray(detail?.errors) ? detail.errors : [];
  list.forEach((entry) => {
    const field = Array.isArray(entry) ? entry[0] : entry?.field;
    const code = Array.isArray(entry) ? entry[1] : entry?.code || entry?.msg;
    if (!field) return;
    errors[field] = statutoryCodeMessage(code, String(code || "Invalid value"));
  });

  const message = typeof detail === "string" ? detail : String(detail?.message || "");
  const lower = message.toLowerCase();
  if (!errors.uan_number && lower.includes("uan") && (lower.includes("in use") || lower.includes("already"))) {
    errors.uan_number = STATUTORY_CODE_MESSAGES.uan_duplicate;
  }
  if (!errors.pan && lower.includes("pan") && (lower.includes("in use") || lower.includes("already"))) {
    errors.pan = STATUTORY_CODE_MESSAGES.pan_duplicate;
  }
  if (!errors.aadhaar && lower.includes("aadhaar") && (lower.includes("in use") || lower.includes("already"))) {
    errors.aadhaar = STATUTORY_CODE_MESSAGES.aadhaar_duplicate;
  }
  if (!errors.pf_number && lower.includes("pf") && (lower.includes("in use") || lower.includes("already"))) {
    errors.pf_number = "This PF Member ID is already in use in your organisation";
  }
  return errors;
}
